import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type {
  IInputTask,
  IInputTaskApplication,
  IInputTaskCropEstablishment,
} from 'interfaces/IInputTask';

import { isApplicationLikeTask, taskHasLiquidInput } from './stepCalculations';

export type TaskDetailField = 'waterRate' | 'target' | 'notes';

export interface TaskDetailFieldState {
  /** Set for task types with Water rate and Target (Application, Crop Establishment). */
  applicationTask: IInputTaskApplication | IInputTaskCropEstablishment | null;
  hasWaterRate: boolean;
  hasTarget: boolean;
  hasNotes: boolean;
  showWaterRate: boolean;
  showTarget: boolean;
  showNotes: boolean;
}

/**
 * Which Application Details fields a task has and shows. Each field shows
 * when the user has toggled it on, or by default when it holds a value (or,
 * for Water rate, when the task has a liquid input). Shared by the task row,
 * where Water rate and Target sit, and the Notes line beneath it (OP-2534).
 */
export const getTaskDetailFieldState = (
  task: IInputTask,
  gUnits: GUnit[],
  fieldVisibilityOverride: Map<string, boolean>
): TaskDetailFieldState => {
  const applicationTask = isApplicationLikeTask(task) ? task : null;
  const hasWaterRate = !!applicationTask && applicationTask.waterRate !== null;
  const hasTarget =
    !!applicationTask && applicationTask.targetSpecies.length > 0;
  const hasNotes = !!task.notes;
  const override = (field: TaskDetailField) =>
    fieldVisibilityOverride.get(`${task.taskId}:${field}`);

  return {
    applicationTask,
    hasWaterRate,
    hasTarget,
    hasNotes,
    showWaterRate:
      !!applicationTask &&
      (override('waterRate') ??
        (hasWaterRate || taskHasLiquidInput(task, gUnits))),
    showTarget: !!applicationTask && (override('target') ?? hasTarget),
    showNotes: override('notes') ?? hasNotes,
  };
};
